import ModalComponent from "../../../Components/ModalComponent";
import { Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../apis/store";
import { useMemo, useState } from "react";
import { toPrintOrder } from "../../../apis/actions/appointments.actions";
import { useTranslation } from "react-i18next";
import "moment/locale/ar";
import {
  displayField,
  formatMoney,
  formatOrderDateTime,
  isPresentField,
  orderStateLabel,
  paymentMethodLabel,
} from "../../../utils/orderDisplay";

interface MyOrdersDetailsProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  selectedData: any;
  type?: string;
}

const MyOrdersDetails = ({
  showModal,
  setShowModal,
  selectedData,
}: MyOrdersDetailsProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingPrint, setLoadingPrint] = useState(false);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const font = isRtl ? " ac--font-expo" : "";

  const transactions = useMemo(
    () => selectedData?.transaction_ids ?? [],
    [selectedData]
  );

  const lines = selectedData?.order_line ?? [];

  const statusClass =
    selectedData?.order_state === "done"
      ? "ac-order-detail-modal__status-badge--done"
      : "ac-order-detail-modal__status-badge--progress";

  const handlePrint = async () => {
    if (!selectedData?.access_token) return;
    setLoadingPrint(true);
    try {
      await dispatch(
        toPrintOrder({
          id: selectedData.access_token,
          lang: i18n.language,
        })
      );
    } finally {
      setLoadingPrint(false);
    }
  };

  if (!selectedData) return null;


  return (
    <ModalComponent
      title={null}
      open={showModal}
      onClose={() => setShowModal(false)}
      closeOnOutsideClick
      width={560}
      modalStyle={{ top: 120 }}
      showCloseIcon
    >
      <div className={`ac-invoice-detail-modal ac-order-detail-modal${font}`}>
        <header className="ac-invoice-detail-modal__header">
          <div className="ac-order-detail-modal__title-row">
            <h2 className={`ac-invoice-detail-modal__title${font}`}>
              {t("profile.order.orderDetails.title")}
            </h2>
            <span
              className={`ac-order-detail-modal__status-badge ${statusClass}${font}`}
            >
              {orderStateLabel(selectedData.order_state, t)}
            </span>
          </div>
          <Button
            type="default"
            className={`ac-invoice-detail-modal__print${font}`}
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={loadingPrint}
            disabled={!selectedData?.access_token}
          >
            {t("profile.order.print")}
          </Button>
        </header>

        <section
          className="ac-invoice-detail-modal__summary"
          aria-label={t("profile.order.orderDetails.title")}
        >
          <div className="ac-invoice-detail-modal__summary-item">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.order.orderNumber")}
            </span>
            <span className={`ac-invoice-detail-modal__value${font}`}>
              {displayField(selectedData.name)}
            </span>
          </div>
          <div className="ac-invoice-detail-modal__summary-item">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.order.date")}
            </span>
            <span className={`ac-invoice-detail-modal__value${font}`}>
              {formatOrderDateTime(selectedData.date_order, i18n.language)}
            </span>
          </div>
          <div className="ac-invoice-detail-modal__summary-item">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.order.orderDetails.branch")}
            </span>
            <span className={`ac-invoice-detail-modal__value${font}`}>
              {displayField(selectedData.branch_id || selectedData.company_id)}
            </span>
          </div>
          {isPresentField(selectedData.vehicle_id) ? (
            <div className="ac-invoice-detail-modal__summary-item">
              <span className={`ac-invoice-detail-modal__label${font}`}>
                {t("profile.order.orderDetails.vehicle")}
              </span>
              <span className={`ac-invoice-detail-modal__value${font}`}>
                {selectedData.vehicle_id}
              </span>
            </div>
          ) : null}
          {selectedData.contain_service ? (
            <div className="ac-invoice-detail-modal__summary-item">
              <span className={`ac-invoice-detail-modal__label${font}`}>
                {t("profile.order.orderDetails.appointmentSlot")}
              </span>
              <span className={`ac-invoice-detail-modal__value${font}`}>
                {isPresentField(selectedData.appointment_slot_id)
                  ? formatOrderDateTime(
                      selectedData.appointment_slot_id,
                      i18n.language
                    )
                  : t("profile.order.orderDetails.noAppointment")}
              </span>
            </div>
          ) : null}
          {transactions.length > 0 ? (
            <div className="ac-invoice-detail-modal__summary-item">
              <span className={`ac-invoice-detail-modal__label${font}`}>
                {t("profile.order.cart.paymentThrough")}
              </span>
              <span className={`ac-invoice-detail-modal__value${font}`}>
                <ul className="ac-order-detail-modal__payments">
                  {transactions.map((item: any, index: number) => (
                    <li key={`${item.payment_method_id}-${index}`}>
                      <span className="icon-saudi_riyal">
                        {formatMoney(item.amount)}
                      </span>{" "}
                      {paymentMethodLabel(item.payment_method_id, t)}
                    </li>
                  ))}
                </ul>
              </span>
            </div>
          ) : null}
          <div className="ac-invoice-detail-modal__summary-item ac-invoice-detail-modal__summary-item--total">
            <span className={`ac-invoice-detail-modal__label${font}`}>
              {t("profile.order.cart.total")} ({t("profile.order.cart.vat")})
            </span>
            <span
              className={`ac-invoice-detail-modal__value ac-invoice-detail-modal__value--total icon-saudi_riyal${font}`}
            >
              {formatMoney(selectedData.amount_total)}
            </span>
          </div>
        </section>

        {lines.length > 0 ? (
          <section>
            <h3 className={`ac-invoice-detail-modal__section-title${font}`}>
              {t("profile.order.orderDetails.lineItems")}
            </h3>
            <ul className="ac-invoice-detail-modal__lines">
              {lines.map((line: any) => {
                const qty = Number(line.product_uom_qty) || 1;
                return (
                  <li key={line.id} className="ac-order-detail-modal__item">
                    <div>
                      <p className={`ac-invoice-detail-modal__line-title${font}`}>
                        {line.name?.trim()}
                      </p>
                      <span className={`ac-invoice-detail-modal__line-meta${font}`}>
                        {t("profile.order.orderDetails.quantity")}: {qty} ×{" "}
                        <span className="icon-saudi_riyal">
                          {formatMoney(line.price_unit)}
                        </span>
                      </span>
                    </div>
                    {/* ponytail: price_unit is VAT-inclusive, price_subtotal is not; qty × unit keeps the row consistent */}
                    <span className={`ac-order-detail-modal__item-total icon-saudi_riyal${font}`}>
                      {formatMoney(qty * Number(line.price_unit))}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>
    </ModalComponent>
  );
};

export default MyOrdersDetails;
